import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { AssetAmount } from "@sundaeswap/asset";
import { AssetAmountField } from "@/components/AssetAmountField";
import { DepositStepHeader } from "./DepositStepHeader";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { collectMarketWalletIds } from "@/lib/marketUtils";

export interface DepositEntry {
  id: string;
  assetId: string;
  amount: string;
}

export interface WalletToken {
  assetId: string;
  name: string;
  balance: string;
  decimals: number;
}

interface AmountStepProps {
  entries: DepositEntry[];
  onEntriesChange: (entries: DepositEntry[]) => void;
  onConfirm: () => void;
  onBack?: () => void;
}

function createEntryId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createDepositEntry(assetId = "ada.lovelace"): DepositEntry {
  return {
    id: createEntryId(),
    assetId,
    amount: "",
  };
}

function parseAmountToAssetAmount(
  amount: string,
  assetId: string,
  decimals: number,
): AssetAmount<typeof assetId> | null {
  const normalized = amount.trim();
  if (!normalized) return null;
  if (!/^\d*\.?\d*$/.test(normalized)) return null;
  return AssetAmount.fromValue(Number(normalized), { assetId, decimals });
}

export const AmountStep: FC<AmountStepProps> = ({
  entries,
  onEntriesChange,
  onConfirm,
  onBack,
}) => {
  const { adaBalance, balance } = useWalletObserver();
  const { markets } = useGummiwormContext();
  const marketWalletIds = useMemo(() => collectMarketWalletIds(markets), [markets]);
  const [shakingRows, setShakingRows] = useState<string[]>([]);
  const [enteringRowId, setEnteringRowId] = useState<string | null>(null);
  const [exitingRowIds, setExitingRowIds] = useState<Set<string>>(new Set());
  const previousInvalidRowsRef = useRef<Set<string>>(new Set());
  const enterTimeoutRef = useRef<number | null>(null);
  const removeTimeoutsRef = useRef<Map<string, number>>(new Map());

  const walletTokens = useMemo<WalletToken[]>(() => {
    const tokens: WalletToken[] = [];
    if (marketWalletIds.has("ada.lovelace")) {
      tokens.push({
        assetId: "ada.lovelace",
        name: "ADA",
        balance: adaBalance.value.toString(),
        decimals: 6,
      });
    }
    if (balance) {
      for (const [id, amt] of balance) {
        if (id === "ada.lovelace") continue;
        if (!marketWalletIds.has(id)) continue;
        // Skip NFTs (amount = 1 with 0 decimals)
        if (amt.decimals === 0 && amt.amount <= 1n) continue;
        const name = amt.metadata?.name || (() => {
          const hexName = id.split(".")[1] ?? "";
          try { return Buffer.from(hexName, "hex").toString("utf-8"); }
          catch { return hexName.slice(0, 8); }
        })() || "Unknown";
        tokens.push({
          assetId: id,
          name,
          balance: String(amt.value),
          decimals: amt.decimals || 0,
        });
      }
    }
    return tokens;
  }, [adaBalance, balance, marketWalletIds]);

  const tokenMap = useMemo(
    () => new Map(walletTokens.map((t) => [t.assetId, t])),
    [walletTokens],
  );

  const usedAssetIds = useMemo(
    () => new Set(entries.map((e) => e.assetId)),
    [entries],
  );

  const updateEntry = useCallback(
    (entryId: string, patch: Partial<DepositEntry>) => {
      const next = entries.map((e) => (e.id === entryId ? { ...e, ...patch } : e));
      onEntriesChange(next);
    },
    [entries, onEntriesChange],
  );

  const removeEntry = useCallback(
    (entryId: string) => {
      if (entries.length <= 1) return;
      setExitingRowIds((current) => new Set(current).add(entryId));

      const timeout = window.setTimeout(() => {
        onEntriesChange(entries.filter((entry) => entry.id !== entryId));
        setExitingRowIds((current) => {
          const next = new Set(current);
          next.delete(entryId);
          return next;
        });
        removeTimeoutsRef.current.delete(entryId);
      }, 220);

      removeTimeoutsRef.current.set(entryId, timeout);
    },
    [entries, onEntriesChange],
  );

  const addEntry = useCallback(() => {
    const unused = walletTokens.find((t) => !usedAssetIds.has(t.assetId));
    if (!unused) return;
    const nextEntry = createDepositEntry(unused.assetId);
    onEntriesChange([...entries, nextEntry]);
    setEnteringRowId(nextEntry.id);
  }, [entries, walletTokens, usedAssetIds, onEntriesChange]);

  const hasValidEntry = entries.some((e) => Number(e.amount) > 0);
  const canAddMore = walletTokens.length > entries.length;
  const invalidRows = useMemo(() => {
    const next = new Set<string>();

    entries.forEach((entry) => {
      const token = tokenMap.get(entry.assetId);
      if (!token) return;

      const requestedAmount = parseAmountToAssetAmount(
        entry.amount,
        token.assetId,
        token.decimals,
      );
      if (!requestedAmount) return;
      const availableAmount = parseAmountToAssetAmount(
        token.balance,
        token.assetId,
        token.decimals,
      );
      if (!availableAmount) return;

      if (requestedAmount.amount > availableAmount.amount) {
        next.add(entry.id);
      }
    });

    return next;
  }, [entries, tokenMap]);
  const canReviewDeposit = hasValidEntry && invalidRows.size === 0;

  useEffect(() => {
    const previousInvalidRows = previousInvalidRowsRef.current;
    const newlyInvalidRows = Array.from(invalidRows).filter(
      (entryId) => !previousInvalidRows.has(entryId),
    );

    if (newlyInvalidRows.length === 0) {
      previousInvalidRowsRef.current = new Set(invalidRows);
      return;
    }

    setShakingRows((current) => Array.from(new Set([...current, ...newlyInvalidRows])));

    const timeout = window.setTimeout(() => {
      setShakingRows((current) =>
        current.filter((entryId) => !newlyInvalidRows.includes(entryId)),
      );
    }, 360);

    previousInvalidRowsRef.current = new Set(invalidRows);

    return () => window.clearTimeout(timeout);
  }, [invalidRows]);

  useEffect(() => {
    if (!enteringRowId) return;

    if (enterTimeoutRef.current) {
      window.clearTimeout(enterTimeoutRef.current);
    }

    enterTimeoutRef.current = window.setTimeout(() => {
      setEnteringRowId(null);
      enterTimeoutRef.current = null;
    }, 240);

    return () => {
      if (enterTimeoutRef.current) {
        window.clearTimeout(enterTimeoutRef.current);
        enterTimeoutRef.current = null;
      }
    };
  }, [enteringRowId]);

  useEffect(() => {
    return () => {
      if (enterTimeoutRef.current) {
        window.clearTimeout(enterTimeoutRef.current);
      }
      removeTimeoutsRef.current.forEach((timeout) => window.clearTimeout(timeout));
    };
  }, []);

  return (
    <>
      {/* Header */}
      <DepositStepHeader
        title="What would you like to deposit?"
        description="Add one or more tokens to move into your trading balance."
      />

      {/* Deposit rows */}
      <div className="w-full">
        <div className="flex max-h-[34rem] w-full flex-col gap-2 overflow-y-auto pr-1">
          {entries.map((entry, i) => {
            const token = tokenMap.get(entry.assetId);
            const tokenBalance = token?.balance ?? "0";
            const isOverBalance = invalidRows.has(entry.id);
            const isShaking = shakingRows.includes(entry.id);
            const isEntering = enteringRowId === entry.id;
            const isExiting = exitingRowIds.has(entry.id);

            return (
              <div
                key={entry.id}
                className={`group w-full shrink-0 overflow-hidden ${isEntering ? "sr-row-enter" : ""} ${isExiting ? "sr-row-exit" : ""}`}
              >
                <AssetAmountField
                  amount={entry.amount}
                  label="Deposit Amount"
                  onAmountChange={(value) => updateEntry(entry.id, { amount: value })}
                  token={token ?? walletTokens[0]}
                  tokens={walletTokens}
                  usedAssetIds={usedAssetIds}
                  onSelectToken={(assetId) => updateEntry(entry.id, { assetId })}
                  errorText={isOverBalance ? "Amount exceeds wallet balance." : undefined}
                  isError={isOverBalance}
                  isShaking={isShaking}
                  autoFocus={i === 0 || enteringRowId === entry.id}
                  amountClassName="text-[28px] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  footerText={(
                    <>
                      <span>Available in wallet:</span>
                      <br />
                      <span className="text-sr-bubblegum">
                        {tokenBalance} {token?.name ?? ""}
                      </span>
                    </>
                  )}
                  actionSlot={entries.length > 1 ? (
                    <button
                      onClick={() => removeEntry(entry.id)}
                      className="absolute right-full top-1/2 mr-2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-sr-surface text-sr-neutral-700 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 hover:text-sr-salmon focus-visible:opacity-100"
                      aria-label="Remove token"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6H21" />
                        <path d="M8 6V4H16V6" />
                        <path d="M19 6L18 20H6L5 6" />
                        <path d="M10 11V17" />
                        <path d="M14 11V17" />
                      </svg>
                    </button>
                  ) : undefined}
                />
              </div>
            );
          })}

          {/* Add Token */}
          {canAddMore && (
            <button
              onClick={addEntry}
              className="w-full shrink-0 rounded-[24px] bg-sr-pink-glow p-3.5 text-sm font-medium transition-colors hover:bg-sr-pink-glow/70"
              style={{ color: "#F888E4" }}
            >
              Add Token +
            </button>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="flex w-full items-center gap-3">
        {onBack && (
          <button
            className="sr-surface-pill flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full text-sr-neutral-300 transition-colors hover:text-white"
            onClick={onBack}
            aria-label="Go back"
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
        <button
          className="sr-btn-primary flex-1"
          disabled={!canReviewDeposit}
          onClick={onConfirm}
        >
          Review Deposit
        </button>
      </div>
    </>
  );
};
